import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { object } from "prop-types";
import {
  workFactorIdToAbbrev,
  workFactorAbbrevToId,
} from "containers/AssessmentSettingsWAA/constants";
import AverageScoreScale from "components/DynamicScoreReportScales/AverageScoreScale";
import { getTopFourWorkFactors } from "../ScoreReportHelpers";

const WAA = (props) => {
  // ---------------------------- Prop Deconstruction ---------------------------
  const {
    candidateInfo,
    cDetails,
    reportText,
    SelectedScoreRangeSection,
    testScores,
    WaaWithoutWorkFactorsAlert,
    workFactors,
  } = props;

  // ---------------------------- States ----------------------------
  const [workFactorsByImportance, setWorkFactorsByImportance] = useState([]);
  const [mappedWorkFactors, setMappedWorkFactors] = useState(new Map());
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [candidateFirstName, setCandidateFirstName] = useState("");

  // ---------------------------- useEffect Hooks ----------------------------
  useEffect(() => {
    if (workFactors) {
      const newMappedWorkFactors = new Map();
      workFactors.forEach((wf) =>
        newMappedWorkFactors.set(wf.get("column"), wf)
      );
      setMappedWorkFactors(newMappedWorkFactors);
    }

    if (testScores) {
      const newInterviewQuestions = JSON.parse(
        testScores.get("interviewQuestions")
      );

      setInterviewQuestions(newInterviewQuestions);
    }

    if (cDetails) {
      // Sorts the work factors by company value
      const sortedWorkFactors = Object.keys(workFactorAbbrevToId).sort(
        (a, b) =>
          cDetails.get(a) === cDetails.get(b)
            ? a.localeCompare(b)
            : Number(cDetails.get(b)) - Number(cDetails.get(a))
      );
      setWorkFactorsByImportance(sortedWorkFactors);
    }

    if (candidateInfo) {
      setCandidateFirstName(candidateInfo.get("testTakerFirstName"));
    }
  }, []);

  // ---------------------------- Results Summary ----------------------------
  const getResultSummary = () => (
    <Row>
      <Col>
        <h3 className="reportHeader mt-4">Results Summary</h3>
        {testScores ? (
          <div className="px-4">
            <AverageScoreScale
              score={Number(testScores.get("percentile"))}
              testId={50}
            />
          </div>
        ) : null}
        <div className="mx-4 mt-5">
          {SelectedScoreRangeSection}
          <hr className="divider mt-4" />
        </div>
        <div className="pt-3">
          <p>
            {`${candidateFirstName}'s overall Workplace Alignment score was higher than
        3% of other people who have completed this assessment previously, which
        indicates that they are likely to:`}
          </p>
        </div>
        <ul className="double-list">
          {reportText.get("summary").map((list, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{list}</li>
          ))}
        </ul>
        <hr className="divider mt-4" />
      </Col>
    </Row>
  );

  // ---------------------------- Results Details ----------------------------
  const getResultsDetailsText = () => (
    <Row>
      <Col>
        <h3 className="reportHeader">Results Details</h3>
        {getTopFourWorkFactors(
          testScores,
          candidateFirstName,
          mappedWorkFactors,
          "score-report-results-details-list"
        )}
        <hr className="divider my-4" />
      </Col>
    </Row>
  );

  // ---------------------------- Work Factors Profile Match ----------------------------

  const getWorkFactorsProfileMatch = () => {
    const rows = workFactorsByImportance.map((traitAbbrev) =>
      getTraitRow(traitAbbrev)
    );
    return (
      <Row>
        <Col>
          <h3 className="reportHeader mb-3">Work Factors Profile Match</h3>
          {getTableKey()}
          <table className="table">
            {getTableHeader()}
            <tbody>{rows}</tbody>
          </table>
          <hr className="divider my-4" />
        </Col>
      </Row>
    );
  };

  // ---------------------------- Interview Questions ----------------------------
  const getInterviewGuide = () => {
    const tableRows = interviewQuestions.map((traitObj) => {
      const traitAbbrev = workFactorIdToAbbrev[traitObj.itemId];

      return (
        <React.Fragment key={traitObj.itemId}>
          {getTraitRow(traitAbbrev, true)}
          <tr>
            <td colSpan="3">
              <strong>Interview Question: {traitObj.question}</strong>
            </td>
          </tr>
        </React.Fragment>
      );
    });

    return (
      <Row>
        <Col>
          <h3 className="reportHeader mb-3">Interview Questions</h3>
          <p>{reportText && reportText.get("interviewPreface")}</p>
          <p>
            If {candidateFirstName} proceeds to the interview stage, you may
            choose to further investigate their response in these areas:{" "}
          </p>
          {getTableKey()}
          <table className="table">
            {getTableHeader()}
            <tbody>{tableRows}</tbody>
          </table>
          <hr className="divider my-4" />
        </Col>
      </Row>
    );
  };

  // ---------------------------- General Helpers ----------------------------
  const getTraitRow = (traitAbbrev, isInterviewQuestions) => {
    const workFactor = mappedWorkFactors.get(traitAbbrev);
    if (!workFactor) {
      return null;
    }

    const traitScore = testScores.get(traitAbbrev);
    const organizationScore = cDetails ? Number(cDetails.get(traitAbbrev)) : 0;
    const name = workFactor.get("name");
    const description = workFactor.get("description");

    return (
      <tr
        key={traitAbbrev}
        style={{
          backgroundColor: isInterviewQuestions ? "#F7F7F7" : "",
        }}
      >
        <td className="font-weight-bold">{name}</td>
        <td className={isInterviewQuestions ? "font-weight-bold" : ""}>
          {description}
        </td>
        <td>
          {getLevelOfImportanceBar(organizationScore)}
          {getLevelOfImportanceBar(traitScore, true)}
        </td>
      </tr>
    );
  };

  const getLevelOfImportanceBar = (rankNumber, candidateWorkFactor) => (
    <div
      className={`${
        candidateWorkFactor ? "candidate" : "organization"
      }-work-factor-bg-color level-of-importance-bar mb-3`}
      style={{
        width: `${rankNumber * 70}px`,
      }}
    />
  );

  const getTableHeader = () => (
    <>
      <colgroup>
        <col span="1" style={{ width: "90px" }} />
        <col span="1" style={{ width: "180px" }} />
        <col span="1" style={{ width: "350px" }} />
      </colgroup>
      <thead>
        <tr
          style={{
            color: "#53565A",
          }}
        >
          <th>Work Factor</th>
          <th>Definition</th>
          <th>Level of Importance</th>
        </tr>
      </thead>
    </>
  );

  const getTableKey = () => (
    <div className="waa-level-of-importance-key">
      <span className="mr-2 d-flex">
        <div className="organization-work-factor-bg-color work-factor-key-circle" />
        Organization Work Factors
      </span>
      <span className="mr-2 d-flex">
        <div className="candidate-work-factor-bg-color work-factor-key-circle" />
        Candidate Work Factors
      </span>
    </div>
  );

  return !testScores ? (
    WaaWithoutWorkFactorsAlert
  ) : (
    <div>
      {getResultSummary()}
      {getResultsDetailsText()}
      {getWorkFactorsProfileMatch()}
      {getInterviewGuide()}
    </div>
  );
};

WAA.propTypes = {
  candidateInfo: object,
  cDetails: object,
  reportText: object,
  SelectedScoreRangeSection: object,
  testScores: object,
  workFactors: object,
  WaaWithoutWorkFactorsAlert: object,
};

export default WAA;
